import { LAYOUT_TYPES } from "../Contexts/DimensionsContext";

/**
 * tapWordAnchor — where the tap word sits, in SCREEN pixels, for every popup.
 *
 * ── Why a published measurement and not a shared formula ──
 *
 * The intro popup is plain PIXI over the whole screen: its word is sized from
 * the popup background's width and placed from the background rect, and in
 * landscape its line is measured from the bottom of the slide card. Every other
 * popup is a SPINE: its word sits on the artist's bone, in spine units, at a
 * size that the spine's own scale then multiplies.
 *
 * The two had nothing in common, so they could only line up by luck — and the
 * spine ones carried a hand-tuned `buttonYOffset` per device, in four separate
 * controllers, tuned against total win rather than against the start page.
 * Mina, demo 2: "Make 'TAP TO START/CONTINUE' same size and position on all
 * screens. Copy from start page."
 *
 * Reproducing the intro's maths elsewhere would mean copying its background
 * fit, its card aspect and its two orientation configs — a second copy of a
 * layout that is still being tuned. So the intro simply PUBLISHES what it
 * measured, and the spine popups solve back to it at their own scale.
 *
 * That is safe here for one specific reason: the intro popup is mounted for the
 * whole session. `introMounted` is only ever set false when init FAILS (see
 * loadingProgressSlice), so it re-lays out on every resize and what is
 * published is never stale. If that ever changes, this goes stale silently —
 * hence `getTapWordAnchor` returning null rather than a guess, and every
 * consumer keeping its own behaviour when it does.
 */

/**
 * How far BELOW its measured line the tap word sits, PER LAYOUT.
 *
 * Keyed on LAYOUT_TYPES — the resolved layout from DimensionsContext, which is
 * what every other responsive table in this game keys on. It is finer than
 * device + orientation because it also splits desktop four ways, and it is
 * exported for exactly this purpose.
 *
 * The value is a multiple of the word's own font size, not a pixel count. The
 * popups run from a small phone in landscape to an ultra-wide monitor, and a
 * fixed nudge that reads as "a couple of pixels" on one is invisible on
 * another. Scaled by the font, the gap keeps its proportion.
 *
 * Why it exists: on a phone in landscape the word sat closer to the value than
 * the "0X BET" line above it did, so the number looked pushed down rather than
 * centred between the two. Vladimir, 2026-09-22: "push it downwards on all of
 * them, even the intro popup just a couple pixels so xbet and tap to continue
 * have same space from the number" — landscape only, at first.
 *
 * ── The landscape value is now the FLOOR for every layout ──
 *
 * Small laptops showed the same fault, because nothing had been tuned for
 * them. Vladimir, 2026-09-25: "can we set that this value is actually a
 * minimum for all devices and sizes... and then for each other device such as
 * mobile portrait, they have their own specifics". So TAP_WORD_NUDGE_MIN
 * applies everywhere, an unknown layout included, and a layout's own entry can
 * only raise it.
 *
 * ONE table, applied at the intro's own placement AND to what it publishes, so
 * the start page and every spine popup move together by construction. Raising a
 * value pushes the word further from the value above it.
 */
export const TAP_WORD_NUDGE_MIN = 1.2;

export const TAP_WORD_NUDGE = {
  [LAYOUT_TYPES.MOBILE_SMALL_PORTRAIT]: TAP_WORD_NUDGE_MIN,
  [LAYOUT_TYPES.MOBILE_SMALL_LANDSCAPE]: TAP_WORD_NUDGE_MIN,
  [LAYOUT_TYPES.MOBILE_PORTRAIT]: TAP_WORD_NUDGE_MIN,
  [LAYOUT_TYPES.MOBILE_LANDSCAPE]: TAP_WORD_NUDGE_MIN,
  [LAYOUT_TYPES.TABLET_PORTRAIT]: TAP_WORD_NUDGE_MIN,
  [LAYOUT_TYPES.TABLET_LANDSCAPE]: TAP_WORD_NUDGE_MIN,
  [LAYOUT_TYPES.SMALL_DESKTOP]: TAP_WORD_NUDGE_MIN,
  [LAYOUT_TYPES.DESKTOP]: TAP_WORD_NUDGE_MIN,
  [LAYOUT_TYPES.WIDE_DESKTOP]: TAP_WORD_NUDGE_MIN,
  [LAYOUT_TYPES.ULTRA_WIDE_DESKTOP]: TAP_WORD_NUDGE_MIN,
};

/**
 * The nudge in screen pixels.
 *
 * Never below TAP_WORD_NUDGE_MIN, whatever the table says — an unknown layout
 * gets the floor too, so a new layout key cannot bring the word back onto the
 * number.
 *
 * @param {number} fontSize The word's rendered size, in screen pixels.
 * @param {string} layout   A LAYOUT_TYPES key, from DimensionsContext.
 */
export const tapWordNudge = (fontSize, layout) =>
  (fontSize > 0 ? fontSize : 0) *
  Math.max(TAP_WORD_NUDGE_MIN, TAP_WORD_NUDGE[layout] ?? TAP_WORD_NUDGE_MIN);

let anchor = null;

/**
 * Called by the intro popup at the end of every layout pass.
 *
 * @param {number} y        The word's centre, in screen pixels.
 * @param {number} fontSize The size it is actually rendered at, in screen px.
 */
export const publishTapWordAnchor = ({ y, fontSize }) => {
  if (!Number.isFinite(y) || !(fontSize > 0)) return;
  anchor = { y, fontSize };
};

/**
 * The last measured intro geometry, or null before the intro has laid out.
 *
 * Null is a real answer and must be handled: a popup that opens before the
 * intro has measured — which nothing currently does, since the intro is the
 * first thing on screen — keeps whatever its own rig gave it.
 */
export const getTapWordAnchor = () => anchor;

/** Test seam. */
export const resetTapWordAnchor = () => {
  anchor = null;
};
